import React, { useMemo, useState } from "react";
import { Button, Space, Table, Input, Tooltip, message, Card, Typography, Tag, Empty, Segmented } from "antd";
import type { TableColumnsType } from "antd";
import { CarOutlined, RedoOutlined, SearchOutlined, EnvironmentOutlined, ClockCircleOutlined } from "@ant-design/icons";
import "../style/Entetrprise.css";

const { Title, Text } = Typography;

const PURPLE = "#89339c";

interface CabType {
    key: string;
    cab_id: string;
    vehiclename: string;
    Location: string;
    waitingtime: number;
}

const pageWrapper: React.CSSProperties = {
    padding: "20px",
    background: "linear-gradient(135deg, #f3eef7 0%, #fbf8ff 100%)",
    boxSizing: "border-box",
};

const refreshBtn: React.CSSProperties = {
    height: 44,
    width: 44,
    borderRadius: 12,
    color: "#fff",
    border: "none",
    background: "linear-gradient(135deg,#89339c,#b56ac8)",
};

const INITIAL_DATA: CabType[] = [];

const waitBand = (mins: number) => {
    if (mins <= 5) return { label: "Ready", color: "#237804", bg: "#f0f8ec", bar: "#52c41a" };
    if (mins <= 20) return { label: "Short wait", color: "#ad6800", bg: "#fffbe6", bar: "#faad14" };
    return { label: "Long wait", color: "#a8071a", bg: "#fff1f0", bar: "#f5222d" };
};

const AvailableCabs: React.FC = () => {
    const [dataSource] = useState<CabType[]>(INITIAL_DATA);
    const [searchText, setSearchText] = useState("");
    const [band, setBand] = useState<string>("All");
    const [loading, setLoading] = useState(false);

    const handleRefresh = () => {
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            message.success("Cab list refreshed");
        }, 600);
    };

    const columns: TableColumnsType<CabType> = [
        {
            title: "Cab ID",
            dataIndex: "cab_id",
            key: "cab_id",
            align: "center",
            width: 140,
            fixed: "left",
            render: (text) => <span className="cab-id">{text}</span>,
        },
        {
            title: "Vehicle",
            dataIndex: "vehiclename",
            key: "vehiclename",
            width: 220,
            render: (text) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="cab-avatar"><CarOutlined /></span>
                    <span style={{ fontWeight: 600, color: "#333" }}>{text}</span>
                </div>
            ),
        },
        {
            title: "Location",
            dataIndex: "Location",
            key: "Location",
            width: 220,
            render: (text) => (
                <span className="cab-loc">
                    <EnvironmentOutlined /> {text}
                </span>
            ),
        },
        {
            title: "Waiting Time",
            dataIndex: "waitingtime",
            key: "waitingtime",
            align: "center",
            width: 200,
            render: (mins: number) => {
                const b = waitBand(mins);
                return (
                    <Tooltip title={b.label}>
                        <span className="cab-wait" style={{ color: b.color, background: b.bg }}>
                            <ClockCircleOutlined />
                            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{mins}</span>
                            <span style={{ opacity: 0.7, fontSize: 12 }}>min</span>
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "Status",
            key: "status",
            align: "center",
            width: 140,
            filters: [
                { text: "Ready", value: "Ready" },
                { text: "Short wait", value: "Short wait" },
                { text: "Long wait", value: "Long wait" },
            ],
            onFilter: (value, record) => waitBand(record.waitingtime).label === value,
            render: (_, record) => {
                const b = waitBand(record.waitingtime);
                return (
                    <Tag style={{ borderRadius: 999, color: b.color, background: b.bg, borderColor: b.bar, fontWeight: 600, margin: 0 }}>
                        {b.label}
                    </Tag>
                );
            },
        },
    ];

    const filteredData = useMemo(() => {
        const text = searchText.trim().toLowerCase();
        return dataSource.filter((item) => {
            if (band !== "All" && waitBand(item.waitingtime).label !== band) return false;
            if (!text) return true;
            return (
                item.cab_id?.toLowerCase().includes(text) ||
                item.vehiclename?.toLowerCase().includes(text) ||
                item.Location?.toLowerCase().includes(text)
            );
        });
    }, [dataSource, searchText, band]);

    const counts = useMemo(() => {
        const c = { Ready: 0, "Short wait": 0, "Long wait": 0 } as Record<string, number>;
        dataSource.forEach((d) => { c[waitBand(d.waitingtime).label] += 1; });
        return c;
    }, [dataSource]);

    return (
        <div className="fit-page" style={pageWrapper}>
            <Card
                variant="borderless"
                styles={{ body: { padding: "12px 24px" } }}
                style={{
                    flex: "0 0 auto",
                    background: "#884699",
                    borderRadius: 16,
                    boxShadow: "0 8px 24px rgba(137, 51, 156, 0.15)",
                    marginBottom: 12,
                    padding: "2px 8px",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                    <div>
                        <span className="cab-eyebrow">Fleet · Live</span>
                        <Title level={2} style={{ color: "#fff", margin: "2px 0 0", fontWeight: 700, letterSpacing: "0.5px" }}>
                            Available Cabs
                        </Title>
                    </div>

                    <Space size={8} wrap>
                        <span className="cab-hero-pill"><i style={{ background: "#52c41a" }} /> {counts.Ready} ready</span>
                        <span className="cab-hero-pill"><i style={{ background: "#faad14" }} /> {counts["Short wait"]} short</span>
                        <span className="cab-hero-pill"><i style={{ background: "#f5222d" }} /> {counts["Long wait"]} long</span>
                    </Space>
                </div>
            </Card>

            <div className="toolbar-glass cab-toolbar">
                <Space size={12} wrap>
                    <Segmented
                        value={band}
                        onChange={(v) => setBand(v as string)}
                        options={["All", "Ready", "Short wait", "Long wait"]}
                        size="large"
                    />
                    <Tooltip title="Refresh">
                        <Button icon={<RedoOutlined />} style={refreshBtn} onClick={handleRefresh} loading={loading} />
                    </Tooltip>
                </Space>

                <Space size={12} wrap>
                    <div className="active-pill" style={{ fontSize: 15 }}>
                        <CarOutlined />
                        Showing: {filteredData.length}
                    </div>
                    <Input
                        allowClear
                        prefix={<SearchOutlined style={{ color: "#999" }} />}
                        placeholder="Search cab ID, vehicle, location..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 280, maxWidth: "100%", height: 42, borderRadius: 8 }}
                    />
                </Space>
            </div>

            <div className="fit-table" style={{ background: "#fff", padding: 12, borderRadius: 16, boxShadow: "0 6px 20px rgba(0,0,0,0.03)" }}>
                <Table<CabType>
                    bordered
                    className="enterprise-table cab-table"
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="key"
                    loading={loading}
                    scroll={{ x: 920, y: 240 }}
                    rowClassName={(record) => `wait-${waitBand(record.waitingtime).label.split(" ")[0].toLowerCase()}`}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Text type="secondary">
                                        {searchText || band !== "All" ? "No cabs match your filters." : "No cabs available right now."}
                                    </Text>
                                }
                            />
                        ),
                    }}
                    pagination={{
                        pageSize: 10,
                        pageSizeOptions: ["10", "25", "50", "100"],
                        showSizeChanger: true,
                        showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} cabs`,
                    }}
                />
            </div>

            <style>
                {`
                .cab-eyebrow {
                    display: block; font-size: 11px; font-weight: 600;
                    letter-spacing: 0.14em; text-transform: uppercase;
                    color: rgba(255,255,255,0.7);
                }
                .cab-hero-pill {
                    display: inline-flex; align-items: center; gap: 7px;
                    color: #fff; font-weight: 600; font-size: 13px;
                    background: rgba(255,255,255,0.16);
                    border: 1px solid rgba(255,255,255,0.28);
                    padding: 6px 14px; border-radius: 999px;
                    font-variant-numeric: tabular-nums;
                }
                .cab-hero-pill i { width: 7px; height: 7px; border-radius: 50%; display: block; }

                .cab-toolbar {
                    display: flex; justify-content: space-between;
                    align-items: center; flex-wrap: wrap; gap: 12px;
                }

                .enterprise-table .ant-table-thead > tr > th {
                    background: ${PURPLE} !important;
                    color: #fff !important;
                    text-align: center !important;
                    font-weight: 600 !important;
                    font-size: 12px !important;
                    letter-spacing: 0.05em;
                    text-transform: uppercase;
                }
                .enterprise-table .ant-table-thead > tr > th::before { display: none !important; }
                .enterprise-table .ant-table-column-sorter,
                .enterprise-table .ant-table-filter-trigger { color: rgba(255,255,255,0.6) !important; }
                .enterprise-table .ant-table-column-sorter-up.active,
                .enterprise-table .ant-table-column-sorter-down.active,
                .enterprise-table .ant-table-filter-trigger.active { color: #ffd666 !important; }
                .enterprise-table .ant-table-tbody > tr:hover > td { background: #f3e8f6 !important; }
                .enterprise-table .ant-table-cell-fix-left { background: inherit !important; }

                /* signature: left status rail on every row */
                .cab-table .ant-table-tbody > tr > td:first-child { box-shadow: inset 4px 0 0 var(--bar); }
                .cab-table tr.wait-ready > td:first-child { --bar: #52c41a; }
                .cab-table tr.wait-short > td:first-child { --bar: #faad14; }
                .cab-table tr.wait-long  > td:first-child { --bar: #f5222d; }

                .cab-id {
                    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                    font-weight: 700; font-size: 12.5px; color: ${PURPLE};
                    background: #f6ecf8; border: 1px solid #e8d5ed;
                    padding: 3px 9px; border-radius: 6px; white-space: nowrap;
                }
                .cab-avatar {
                    width: 32px; height: 32px; border-radius: 9px;
                    background: #f3e8f6; color: ${PURPLE};
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 15px; flex-shrink: 0;
                }
                .cab-loc {
                    display: inline-flex; align-items: center; gap: 7px;
                    color: #555; font-size: 13px;
                }
                .cab-wait {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 5px 12px; border-radius: 8px; font-size: 14px;
                }

                @media (max-width: 767px) {
                    .cab-toolbar { flex-direction: column; align-items: stretch; }
                }
                `}
            </style>
        </div>
    );
};

export default AvailableCabs;